import React, { useState } from 'react';
import { Button } from '@/components/common';
import { describeInvitationError } from '@/hooks/useInvitations';
import { countOf } from '@/utils/plural';
import type { CreateInvitationInput } from '@shared/schemas/invitation.schema.js';
import { GUEST_DAY_OPTIONS, RoomRole, type GuestDays } from '@shared/types.js';
import { FaCheckCircle, FaEnvelope, FaExclamationTriangle, FaPaperPlane } from 'react-icons/fa';

/** `player` is a "Resident" wherever a person reads it (§4.1). */
const ROLE_OPTIONS: Array<{ value: RoomRole; label: string; hint: string }> = [
  { value: RoomRole.ADMIN, label: 'Admin', hint: 'Manages the island and its members' },
  { value: RoomRole.PLAYER, label: 'Resident', hint: 'Lives here and can claim a home zone' },
  { value: RoomRole.GUEST, label: 'Guest', hint: 'Visits for a limited time' }
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface InviteFormProps {
  /** Resolves when the invitation exists; rejects with the API failure. */
  onInvite: (input: CreateInvitationInput) => Promise<unknown>;
  disabled?: boolean;
}

/**
 * Email, role, and — for guests only — how long they get. Guests are always
 * time-boxed (§3.1 invariant 3), so the duration is part of the invitation and
 * not something an admin can forget.
 */
export const InviteForm: React.FC<InviteFormProps> = ({ onInvite, disabled = false }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<RoomRole>(RoomRole.PLAYER);
  const [guestDays, setGuestDays] = useState<GuestDays>(7);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSending || disabled) return;

    const trimmed = email.trim();
    if (!EMAIL_PATTERN.test(trimmed)) {
      setError('Enter a valid email address');
      setSuccess(null);
      return;
    }

    setError(null);
    setSuccess(null);
    setIsSending(true);

    try {
      await onInvite(
        role === RoomRole.GUEST
          ? { email: trimmed, role, guestDays }
          : { email: trimmed, role }
      );
      setSuccess(`Invitation sent to ${trimmed}`);
      setEmail('');
    } catch (err) {
      setError(describeInvitationError(err, 'Failed to send the invitation'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    // noValidate: the browser's own bubble for type="email" would pre-empt the
    // inline message, and the same check runs here anyway.
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div>
        <label htmlFor="invite-email" className="block text-sm font-semibold text-white mb-2">
          <span className="flex items-center gap-2">
            <FaEnvelope className="text-game-golden" />
            Email address
          </span>
        </label>
        <input
          id="invite-email"
          name="email"
          type="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            if (error) setError(null);
          }}
          disabled={disabled || isSending}
          placeholder="person@example.com"
          className="w-full px-4 py-2 bg-game-cream border border-game-medium/50 rounded-lg text-gray-900 placeholder-gray-600 focus:border-game-golden focus:outline-none transition-all duration-200 disabled:opacity-50"
        />
      </div>

      <div>
        <span className="block text-sm font-semibold text-white mb-2">Role</span>
        <div className="flex flex-wrap gap-2">
          {ROLE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              title={option.hint}
              disabled={disabled || isSending}
              onClick={() => setRole(option.value)}
              className={`px-3 py-2 rounded-lg text-sm font-semibold border transition-all duration-200 disabled:opacity-50 ${
                role === option.value
                  ? 'bg-game-golden/20 border-game-golden/60 text-white'
                  : 'bg-game-medium/30 border-game-medium/50 text-gray-300 hover:text-white'
              }`}
              aria-pressed={role === option.value}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {role === RoomRole.GUEST && (
        <div>
          <span className="block text-sm font-semibold text-white mb-2">Guest access</span>
          <div className="flex gap-2">
            {GUEST_DAY_OPTIONS.map((days) => (
              <button
                key={days}
                type="button"
                disabled={disabled || isSending}
                onClick={() => setGuestDays(days)}
                aria-pressed={guestDays === days}
                className={`px-3 py-2 rounded-lg text-sm font-semibold border transition-all duration-200 disabled:opacity-50 ${
                  guestDays === days
                    ? 'bg-game-golden/20 border-game-golden/60 text-white'
                    : 'bg-game-medium/30 border-game-medium/50 text-gray-300 hover:text-white'
                }`}
              >
                {countOf(days, 'day')}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="p-3 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-lg text-sm flex items-center gap-2">
          <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="p-3 bg-game-medium/20 border border-game-medium/50 text-green-300 rounded-lg text-sm flex items-center gap-2">
          <FaCheckCircle className="flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      <Button type="submit" variant="primary" isLoading={isSending} disabled={disabled}>
        <FaPaperPlane className="text-sm" />
        <span>Send invitation</span>
      </Button>
    </form>
  );
};
